import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Input, Output, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { MatPaginator } from '@angular/material/paginator';
import { catchError, debounceTime, distinctUntilChanged, finalize, of, Subject, switchMap, takeUntil } from 'rxjs';
import { PaginationResult } from 'src/app/models/common.response';
import { RoomTypeService } from './room-type.service';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';
import { DeleteDialog } from 'src/app/pages/dialogs/delete.component';
import { CommonDropDown } from 'src/app/common/types/dropdown.type';
import { LookupService } from 'src/app/services/lookup.service';
import { IRoomType } from './iroomtype';
import { RoomTypeDetailComponent } from './room-type-detail/room-type-detail.component';
import { PAGE_SIZE_OPTIONS } from 'src/app/common/constant/constants.type';
import { EmptyStateComponent } from 'src/app/shared/empty-state/empty-state.component';


@Component({
  selector: 'app-room-type',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    TranslateModule,
    TablerIconsModule,
    ReactiveFormsModule,
    EmptyStateComponent
  ],
  templateUrl: './room-type.component.html',
  styleUrl: './room-type.component.scss'
})
export class RoomTypeComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @Input() wardId: number | null = null;
  @Output() roomTypeClick = new EventEmitter<IRoomType>();

  displayedColumns: string[] = [
    'name',
    'WardName',
    'MaxBeds',
    'charge',
    'isActive',
    'action',
  ];
  pageSizeOptions = PAGE_SIZE_OPTIONS;

  dataSource = new MatTableDataSource<IRoomType>();
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator =
    Object.create(null);
  private _unsubscribeAll: Subject<void> = new Subject<void>();
  pagination: PaginationResult;
  roomTypecount: number = 0;
  roomtypedata: IRoomType[] = [];
  isLoading: boolean = false;
  searchRoomType: FormControl = new FormControl('');
  WardList: CommonDropDown[] = [];
  billchargeslist: CommonDropDown[] = [];
  selectedWardName: string = '';

  constructor(
    private _roomtypeService: RoomTypeService,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private translate: TranslateService,
    private translationUtils: TranslationUtils,
    private _lookupService: LookupService,
  ) { }

  ngOnInit(): void {

    this._lookupService.billcharges$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((billcharges: CommonDropDown[]) => {
        this.billchargeslist = billcharges;
      });

    this._roomtypeService.pagination$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((pagination: PaginationResult | null) => {
        if (pagination) {
          this.pagination = pagination;
          this.roomTypecount = this.pagination.rowCount;
        }
      });

    this._roomtypeService.roomtypes$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((data) => {
        this.roomtypedata = data;
        this.dataSource = new MatTableDataSource(this.roomtypedata); // Correct initialization
        this.dataSource.paginator = this.paginator;
      });

    this.searchRoomType.valueChanges.pipe(
      takeUntil(this._unsubscribeAll),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((query) => {
        this.isLoading = true;
        const pageSize = this.pagination?.pageSize || 10;
        const requestPayload: any = {
          pageNumber: 0,
          pageSize: pageSize,
          isDescending: false,
          searchValue: query || ''
        };
        if (this.wardId !== null && this.wardId !== undefined) {
          requestPayload.wardId = this.wardId;
        }
        return this._roomtypeService.getAllRoomTypes(requestPayload).pipe(
          finalize(() => (this.isLoading = false)),
          catchError((error) => {
            this.toastr.error(this.translate.instant('COMMON.ERROR'));
            console.error('Error searching room types:', error);
            return of(null);
          })
        );
      })
    ).subscribe((response) => {
      if (response && response.result) {
        this.dataSource.data = response.result.roomTypeDetails;
        this.roomTypecount = response.result.pagination.rowCount;
      }
    });

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['wardId'] && !changes['wardId'].firstChange) {
      if (this.wardId !== null && this.wardId !== undefined) {
        const ward = this.WardList.find(w => w.id === this.wardId);
        this.selectedWardName = ward ? ward.name : '';
        this.refreshRoomType();
      } else {
        this.selectedWardName = '';
        this.refreshRoomType();
      }
    }
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  onAddRoomType() {
    this.dialog.open(RoomTypeDetailComponent, {
      width: '90vh',
      height: 'auto',
      disableClose: true,
      data: {
        wardId: this.wardId
      }
    }).afterClosed()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((result) => {
        if (result) {
          this.refreshRoomType();
        }
      });
  }

  onUpdateRoomType(element: IRoomType) {
    this.dialog.open(RoomTypeDetailComponent, {
      width: '90vh',
      height: 'auto',
      disableClose: true,
      data:
      {
        roomtype: element,
        isEdit: true
      }
    }).afterClosed()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((result) => {
        if (result) {
          this.refreshRoomType();
        }
      });
  }

  onDeleteRoomType(roomTypeId: number) {
    const confirmation = this.dialog.open(DeleteDialog, {
      width: 'auto',
      enterAnimationDuration: '300ms',
      exitAnimationDuration: '300ms',
      disableClose: true,
      data: { message: this.translate.instant('DELETE_DIALOG') + ' ' + this.translate.instant('ENTITY.ROOM_TYPE') + ' ?' }
    })
    confirmation.afterClosed()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((result) => {
        if (result === "confirm") {
          this._roomtypeService.deleteRoomType(roomTypeId)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
              next: (response) => {
                if (response.isSuccess) {
                  this.refreshRoomType();
                  this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.DELETE', this.translate.instant('ENTITY.ROOM_TYPE')));
                }
                else {
                  const allErrors = Object.values(response.errors).flat();
                  this.toastr.error(allErrors.join("\n"));
                }
              },
              error: (error) => {
                this.toastr.error(this.translate.instant('COMMON.ERROR'));
                console.error('Error deleting room type:', error);
              }
            });
        }
      });

  }

  refreshRoomType() {
    const globalSearchValue = this.searchRoomType?.value?.trim();
    const requestPayload: any = {
      pageNumber: 0,
      pageSize: this.pagination?.pageSize || 100,
      isDescending: false
    };

    if (globalSearchValue) {
      requestPayload.SearchValue = globalSearchValue;
    } else {
      requestPayload.pageNumber = this.pagination?.currentPage || 0;
    }

    if (this.wardId !== null && this.wardId !== undefined) {
      requestPayload.wardId = this.wardId;
    }

    this._roomtypeService.getAllRoomTypes(requestPayload)
      .pipe(
        takeUntil(this._unsubscribeAll),
        finalize(() => (this.isLoading = false)),
        catchError((error) => {
          this.toastr.error(this.translate.instant('COMMON.ERROR'));
          console.error('Error refreshing room types:', error);
          return of(null);
        })
      )
      .subscribe((response) => {
        if (response && response.result) {
          const RoomType = response.result.roomTypeDetails;
          this.roomtypedata = RoomType;
          if (!this.dataSource) {
            this.dataSource = new MatTableDataSource(RoomType);
          } else {
            this.dataSource.data = RoomType;
          }
          this.dataSource.paginator = this.paginator;
          this.roomTypecount = response.result.pagination.rowCount;
          this.pagination = response.result.pagination;
        }
      });

  }

  onRowClick(roomType: IRoomType): void {
    this.roomTypeClick.emit(roomType);
  }

  onActionClick(event: Event): void {
    event.stopPropagation();
  }

  getBillCharges(billChargeId: number) {
    if (!this.billchargeslist) {
      return 0;
    }// Ensure the list exists
    const billCharge = this.billchargeslist.find(billCharge => billCharge.id === billChargeId);
    return billCharge ? billCharge?.childValue : 0;
  }

}
